<template>
    <div>
        <main
            v-if="blogData && !notFound"
            class="m-blog m-blog--single"
        >
            <y-container>
                <div
                    class="blog-single"
                    @scroll="handleScroll"
                >
                    <div class="blog-single__left-column">
                        <y-single-hero
                            v-if="blogData"
                            :blog-data="blogData"
                        />

                        <y-single-post-content
                            v-if="blogData && blogData.text"
                            ref="singlePostContent"
                            :headers="headers"
                            :text-editor="blogData.text"
                        />

                        <y-share-in-socials
                            v-if="blogData && blogData.title"
                            :title="blogData.title"
                            :tags="blogData.tagging_tags"
                        />

                        <y-author-info
                            v-if="blogData && blogData.publisher"
                            :publisher="blogData.publisher"
                        />
                    </div>
                    <div class="blog-single__right-column">
                        <y-single-banner
                            v-if="$mq === 'lg' || $mq === 'xlg' "
                            :categories-data="categoriesData"
                            :top-margin="topMargin"
                        />
                    </div>

                    <button
                        v-if="topOfPage"
                        class="back-to-top-button"
                        @click="goUp"
                    >
                        <img
                            src="@/assets/images/up.svg"
                            alt=""
                        >
                    </button>
                </div>
            </y-container>
            <y-container>
                <y-similar-posts
                    v-if="blogData"
                    :posts="blogData.similar_posts_by_tagging"
                />
                <y-email-subscription />
            </y-container>
            <y-container v-if="$mq === 'xs' || $mq === 'sm' || $mq === 'md' ">
                <y-single-banner
                    :categories-data="categoriesData"
                    :top-margin="topMargin"
                />
            </y-container>
        </main>
        <page-not-found v-if="notFound" />
    </div>
</template>

<script >
    import PageMixin from '@groot-nodes/mixins/Page.js';
    import YSingleHero from '@modules/blog/components/SingleHero/SingleHero.vue';
    import YSinglePostContent from '@modules/blog/components/SinglePostContent/SinglePostContent.vue';
    import YShareInSocials from '@modules/blog/components/ShareInSocials/ShareInSocials.vue';
    import YAuthorInfo from '@modules/blog/components/AuthorInfo/AuthorInfo.vue';
    import YSimilarPosts from '@modules/blog/components/SimilarPosts/SimilarPosts.vue';
    import YEmailSubscription from '@modules/blog/components/EmailSubscription/EmailSubscription.vue';
    import YSingleBanner from '@modules/blog/components/SingleBanner/SingleBanner.vue';
    import PageNotFound from '@components/PageNotFound.vue';

    export default {
        name      : 'BlogSinglePage',
        components: {
            PageNotFound,
            YEmailSubscription,
            YSingleHero,
            YSinglePostContent,
            YShareInSocials,
            YAuthorInfo,
            YSimilarPosts,
            YSingleBanner,
        },

        mixins: [PageMixin],

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.heroContent.seo_post_title,

                meta: [
                    {
                        vmid   : 'description',
                        name   : 'description',
                        content: this.heroContent.seo_meta_description,
                    },
                ],
            };
        },

        /**
         *
         * @inheritDoc
         */
        data() {
            return {
                blogData      : null,
                topMargin     : null,
                categoriesData: null,
                heroContent   : '',
                headers       : [],
                scrollPosition: 200,
                topOfPage     : false,
                notFound      : false,
            };
        },

        /**
         * @inheritdoc
         */
        beforeMount() {
            /**
             * listen to amount of scroll
             */
            window.addEventListener('scroll', this.handleScroll);
        },

        methods: {

            /**
             * checking screen got scroll or not
             */
            handleScroll() {
                if (window.pageYOffset < 2500) {
                    if (this.topOfPage) {
                        this.topOfPage = false;
                    }
                } else if (!this.topOfPage) {
                    this.topOfPage = true;
                }
            },

            /**
             * go up
             */
            goUp() {
                window.scrollTo(0, 0);
            },

            /**
             * Get headers from text received from backend
             */
            importHeaders() {
                setTimeout(() => {
                    this.headers = this.$refs.singlePostContent.importHeaders();
                }, 0);
            },

            /**
             * Find page offset of page-content component
             */
            bannerTopMargin() {
                setTimeout(() => {
                    this.topMargin = this.$refs.singlePostContent.bannerTopMargin();
                }, 0);
            },

            /**
             * Check the Promise.all Request Error
             *
             * @param {Array} req - Responses array
             * @param {index} index - number of response
             * @returns {boolean}
             */
            handleReq(req, index) {
                return req && req[index] && !req[index].isAxiosError;
            },

            /**
             * fetch blog single data
             *
             * @returns {Promise}
             */
            fetch() {
                this.$wait.start('loading-single-blog');
                const blogData = this.$services.Content.postSingle({
                    type     : 'blog',
                    slug     : this.$route.params.slug,
                    including: ['id', 'text', 'title', 'image',
                                'header_image', 'created_at', 'synopsis', 'published_at',
                                'publisher', 'tagging_tags', 'tagging_cats', 'short_link',
                                'published_by', 'similar_posts_by_tagging', 'url_encode', 'custom', 'categories', 'slug', 'seo_canonical', 'seo_meta_description', 'seo_post_title'],
                    locale: this.$i18n.locale,
                }).catch((error) => {
                    if (error.response.data.status === '404') {
                        this.notFound = true;
                    }
                    this.handleError(error);
                });

                const categories = this.$services.Tagging.labels({
                    slug     : 'blog-category',
                    including: ['id', 'image', 'title', 'url_encode', 'image_header'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all([blogData, categories])
                    .then((responses) => {
                        if (this.handleReq(responses, 0)) {
                            this.blogData = responses[0].data.results;
                            this.heroContent = this.blogData;
                        }

                        if (this.handleReq(responses, 1)) {
                            this.categoriesData = responses[1].data.results;
                        }

                        this.$nextTick(this.importHeaders);
                        this.$nextTick(this.bannerTopMargin);
                    })
                    .catch((error) => {
                        this.handleError(error);
                    })
                    .finally(() => {
                        this.$wait.end('loading-single-blog');
                    });
            },
        },
    };

</script>

<style lang="scss" src="../scss/blog.scss"/>
